import { useMemo, useState } from 'react'
import { useDroppable } from '@dnd-kit/react'
import { Folder as FolderIcon, MoreVertical, Pencil, Search, Trash2 } from 'lucide-react'
import { cn } from '@/lib/tailwind.utils'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Folder } from '../collections.types'
import { DocumentCountPill } from './document-count-pill'
import { FolderCreatePopover } from './folder-create-popover'

type SortOption = 'name-asc' | 'name-desc' | 'count-desc' | 'count-asc' | 'newest' | 'oldest'

const SORT_LABELS: Record<SortOption, string> = {
  'name-asc': 'Name (A–Z)',
  'name-desc': 'Name (Z–A)',
  'count-desc': 'Most documents',
  'count-asc': 'Fewest documents',
  newest: 'Newest',
  oldest: 'Oldest',
}

function sortFolders(folders: Folder[], sort: SortOption): Folder[] {
  const sorted = [...folders]
  switch (sort) {
    case 'name-asc':
      return sorted.sort((a, b) => a.name.localeCompare(b.name))
    case 'name-desc':
      return sorted.sort((a, b) => b.name.localeCompare(a.name))
    case 'count-desc':
      return sorted.sort((a, b) => b.documentCount - a.documentCount)
    case 'count-asc':
      return sorted.sort((a, b) => a.documentCount - b.documentCount)
    case 'newest':
      return sorted.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    case 'oldest':
      return sorted.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      )
  }
}

type FolderRowProps = {
  folder: Folder
  isSelected: boolean
  onSelect: (folderId: string) => void
  onRename: (folder: Folder) => void
  onDelete: (folder: Folder) => void
}

const FolderRow = ({ folder, isSelected, onSelect, onRename, onDelete }: FolderRowProps) => {
  const { ref, isDropTarget } = useDroppable({
    id: `folder-row-${folder.id}`,
    accept: 'document',
    data: { folderId: folder.id },
  })

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(folder.id)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(folder.id)}
      className={cn(
        'group flex items-center gap-2.5 px-2 py-2 rounded-md cursor-pointer text-sm',
        isSelected ? 'bg-accent' : 'hover:bg-accent/60',
        isDropTarget && 'ring-2 ring-inset ring-primary/50 bg-primary/10',
      )}
    >
      <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate" title={folder.name}>
        {folder.name}
      </span>
      <DocumentCountPill count={folder.documentCount} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="p-0.5 rounded hover:bg-background text-muted-foreground hover:text-foreground"
            aria-label={`Collection options for ${folder.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              onRename(folder)
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              onDelete(folder)
            }}
            className="text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

type FoldersSidebarProps = {
  folders: Folder[]
  selectedFolderId: string | null
  onSelect: (folderId: string | null) => void
  onCreate: (name: string) => Promise<boolean>
  createNameError?: string | null
  onClearCreateNameError?: () => void
  onRename: (folder: Folder) => void
  onDelete: (folder: Folder) => void
  isLoading?: boolean
}

export const FoldersSidebar = ({
  folders,
  selectedFolderId,
  onSelect,
  onCreate,
  createNameError,
  onClearCreateNameError,
  onRename,
  onDelete,
  isLoading,
}: FoldersSidebarProps) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortOption>('name-asc')

  // Dropping a document here removes it from its current collection (see page's onDragEnd).
  const { ref: allRef, isDropTarget: isAllDropTarget } = useDroppable({
    id: 'folder-row-all',
    accept: 'document',
    data: { dropzone: 'all' },
  })

  const visibleFolders = useMemo(() => {
    const filtered = search.trim()
      ? folders.filter((f) => f.name.toLowerCase().includes(search.trim().toLowerCase()))
      : folders
    return sortFolders(filtered, sort)
  }, [folders, search, sort])

  return (
    <div className="w-[300px] shrink-0 mt-6 max-h-[calc(100vh-11rem)] rounded-lg border border-border bg-background shadow-sm p-4 hidden xl:flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">
            COLLECTIONS
          </span>
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
            {folders.length}
          </span>
        </div>
        <FolderCreatePopover
          onCreate={onCreate}
          nameError={createNameError}
          onClearNameError={onClearCreateNameError}
        />
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter collections"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 pl-8 text-sm border border-border"
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Sort</span>
        <Select value={sort} onValueChange={(value) => setSort(value as SortOption)}>
          <SelectTrigger className="h-9 text-sm border border-border">
            <SelectValue>{SORT_LABELS[sort]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SORT_LABELS) as SortOption[]).map((option) => (
              <SelectItem key={option} value={option}>
                {SORT_LABELS[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-0.5 mt-1 overflow-y-auto">
        <div
          ref={allRef}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(null)}
          onKeyDown={(e) => e.key === 'Enter' && onSelect(null)}
          className={cn(
            'flex items-center gap-2.5 px-2 py-2 rounded-md cursor-pointer text-sm font-medium',
            selectedFolderId === null ? 'bg-accent' : 'hover:bg-accent/60',
            isAllDropTarget && 'ring-2 ring-inset ring-primary/50 bg-primary/10',
          )}
        >
          All documents
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2 mt-1">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : visibleFolders.length === 0 ? (
          <p className="text-sm text-muted-foreground px-2 py-4 text-center">
            {folders.length === 0 ? 'No collections yet.' : 'No collections match your search.'}
          </p>
        ) : (
          visibleFolders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              isSelected={selectedFolderId === folder.id}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  )
}
